import { toast } from "@hortis/ui/sonner";
import { flow } from "@mobily/ts-belt";
import { useNavigate } from "@tanstack/react-router";
import { centerOfMass } from "@turf/turf";
import type { Feature, Polygon } from "geojson";
import { type MapMouseEvent } from "mapbox-gl";
import { type MapGeoJSONFeature } from "maplibre-gl";
import { useCallback, useRef, useState } from "react";
import { useMapOptionsStore } from "src/features/collection-map/components/map-options-store";
import type { MapMode } from "../../../features/collection-map/components/types";
import { getFirstFeature } from "../material-clusters/utils";
import { useMaplibre } from "../utils";
import { type LocationPopupProps } from "./location-popup";
import { locationPropertiesSchema } from "./parse-geometry";

interface useLocationBedDrawingProps {
  mapId: string;
  mode: MapMode;
}

export const getFeaturePolygon = flow(
  getFirstFeature,
  (feature): Feature<Polygon, unknown> | undefined =>
    feature?.geometry.type === "Polygon"
      ? (feature as Feature<Polygon, unknown>)
      : undefined,
);

export const useLocationBedDrawing = ({
  mapId,
  mode,
}: useLocationBedDrawingProps) => {
  const { [mapId]: ourMap } = useMaplibre();
  const popupHover = useRef(false);
  const navigate = useNavigate();
  const { locationPolygonsEnabled } = useMapOptionsStore();

  const hoveredPolygonId = useRef<string | number | undefined>(undefined);
  const [popupProps, setPopupProps] = useState<Pick<
    LocationPopupProps,
    "name" | "description" | "latitude" | "longitude"
  > | null>(null);

  const handleClick = useCallback(
    (_: MapMouseEvent, polygon: MapGeoJSONFeature | undefined) => {
      if (mode !== "view") {
        return;
      }

      if (polygon == null) {
        return;
      }

      const location = polygon;

      try {
        const locationProperties = locationPropertiesSchema.parse(
          location.properties,
        );
        const { name } = locationProperties;
        void navigate({
          to: "/site/$siteSlug/map/location/$locationName",
          from: "/site/$siteSlug/map",
          params: {
            locationName: name,
          },
        });
      } catch {
        toast.error("Unable to open location.");
      }
    },
    [mode, navigate],
  );

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      if (ourMap?.getLayer("location-polygon-fill") != null) {
        if (hoveredPolygonId.current != null) {
          ourMap.setFeatureState(
            {
              // eslint-disable-next-line sonarjs/no-duplicate-string
              source: "location-drawing",
              id: hoveredPolygonId.current,
            },
            {
              hover: false,
            },
          );
        }

        const customFeatures = ourMap.queryRenderedFeatures(
          [event.point.x, event.point.y],
          {
            layers: ["location-polygon-fill", "location-polygon-line"],
          },
        );

        const polygon = customFeatures[0];

        if (
          polygon == null ||
          mode === "editLocation" ||
          !locationPolygonsEnabled
        ) {
          setPopupProps(null);
          return;
        }

        const centerCoords = centerOfMass(polygon.geometry);
        const [longitude, latitude] = centerCoords.geometry.coordinates;
        const { name, description } = locationPropertiesSchema.parse(
          polygon.properties,
        );

        hoveredPolygonId.current = polygon.id;

        if (longitude == null || latitude == null) {
          return;
        }
        setPopupProps({
          name,
          description: description ?? "-",
          longitude,
          latitude,
        });

        ourMap.setFeatureState(
          {
            source: "location-drawing",
            id: polygon.id,
          },
          {
            hover: true,
          },
        );
      }
    },
    [locationPolygonsEnabled, mode, ourMap],
  );

  const onMouseLeave = useCallback(() => {
    if (
      ourMap?.getLayer("location-polygon-fill") != null &&
      hoveredPolygonId.current != null
    ) {
      ourMap.setFeatureState(
        {
          source: "location-drawing",
          id: hoveredPolygonId.current,
        },
        {
          hover: false,
        },
      );
      hoveredPolygonId.current = undefined;
    }
  }, [ourMap]);

  return {
    popupProps,
    onMouseMove,
    onMouseLeave,
    handleClick,
    popupHover,
    hoveredPolygonId,
  };
};
